import {
  admin,
  getUser,
  getSession,
  create,
  superAdmin,
  isWorkspaceAdmin,
  hasHubspot,
  hasSalesforce,
} from '@chilipiper/service/src/session'

import {
  Workspace,
  ProductTypes,
  SessionActions,
  Session as ISession,
} from '@chilipiper/api-type-def'

import { session } from '@chilipiper/service/src/api'
import { Products, ProductStatus } from '@chilipiper/api-type-def/src/license'
import { jwtApiV1 } from '@chilipiper/service/src/jwtApiV1'

export enum AccessLevel {
  Admin = 'Admin',
  Master = 'Master',
  User = 'User',
}

export enum AuthProvider {
  Google = 'Google',
  'Microsoft' = 'Microsoft',
  'None' = 'None',
  'Salesforce' = 'Salesforce',
}

export type UILicense = 'Free' | 'Spicy' | 'Hot' | 'Events' | 'Concierge'

export class Session {
  static isAdmin() {
    return admin()
  }

  static isMasterAdmin() {
    return superAdmin()
  }

  static isWorkspaceAdmin() {
    return isWorkspaceAdmin()
  }

  static user() {
    return getUser()
  }

  static isAuthenticated() {
    return jwtApiV1.isAuthenticated()
  }

  static getUserId() {
    const user = getUser()
    if (!user.id) {
      throw new TypeError('No user id')
    }
    return user.id
  }

  static session() {
    return getSession()
  }

  static selectedWorkspace() {
    return getSession().workspaces.selected
  }

  static products() {
    return getSession().products || []
  }

  static hasPlatformConnected() {
    return getSession().isPlatformConnected
  }

  static isCrmConnected() {
    return hasSalesforce() || hasHubspot()
  }

  static hasProductActivated(product: ProductTypes) {
    const products = Session.products()
    if (product === 'Meetings') {
      return products.includes('Meetings') || products.includes('MildMeetings')
    }
    return products.includes(product)
  }

  static isEventsEnabled(): boolean {
    return Session.hasProductActivated('Events')
  }

  static isEnterpriseEnabled(): boolean {
    return Session.hasProductActivated('Enterprise')
  }

  static async createSession() {
    const response = await session()

    create(response)
    return response
  }

  static reload() {
    return Session.createSession()
  }

  static update(data: ISession) {
    create(data)
  }

  static logout() {
    jwtApiV1.logout()
  }

  static workspaceAccessLevel(workspace: Workspace) {
    const session = getSession()

    if (session.admin) {
      return AccessLevel.Master
    }

    if (session.workspaceAdmin) {
      return AccessLevel.Admin
    }

    if (session.workspaces.adminOf?.some(() => workspace.id)) {
      return AccessLevel.Admin
    }

    return AccessLevel.User
  }

  static authProvider() {
    const session = getSession()

    if (session.isGoogleConnected) {
      return AuthProvider.Google
    }

    if (session.isMicrosoftConnected) {
      return AuthProvider.Microsoft
    }

    if (session.isSalesforceConnected) {
      return AuthProvider.Salesforce
    }

    return AuthProvider.None
  }

  static getDomain() {
    return getSession().domain || ''
  }

  static getEmail() {
    return getSession().email || ''
  }

  static getTenantId() {
    return getSession().tenantId || ''
  }

  static getAccountStatus() {
    return getSession().accountInstallationStatus || ''
  }

  static getCurrentUserId() {
    return getSession().user?.id || ''
  }

  static getCrmType() {
    return getSession().crm?.type || ''
  }

  static getDefaultWorkspaceId() {
    return getSession().workspaces.default
  }

  static getActiveLicenses(): UILicense[] {
    const licenses = getSession().licences
    const mappedLicenses = Object.entries(licenses)
      .filter(license => {
        return license[1].status === 'Active'
      })
      .map(license => license[0] as Products)
      .map(license => {
        if (license === 'meetings') {
          return 'Hot'
        }
        if (license === 'mildMeetings') {
          return 'Spicy'
        }
        if (license === 'events') {
          return 'Events'
        }
        if (license === 'concierge') {
          return 'Concierge'
        }
        return 'Free'
      })

    return mappedLicenses.concat(['Free'])
  }

  static getCurrentLicense(): UILicense {
    const licenses = Session.getActiveLicenses()

    if (licenses.includes('Hot')) {
      return 'Hot'
    }

    if (licenses.includes('Spicy')) {
      return 'Spicy'
    }

    if (licenses.includes('Concierge')) {
      return 'Concierge'
    }

    return 'Free'
  }

  static isHotLicenseOnly() {
    const licenses = Session.getActiveLicenses()
    const shouldHaveOnly: UILicense[] = ['Free', 'Hot']
    return shouldHaveOnly.every(l => licenses.includes(l))
  }

  static isFreeUser() {
    const licenses = getSession().licences
    return Object.values(licenses).every(license => license.status !== 'Active')
  }

  static getAssignedWorkspaceId() {
    return getSession().workspaces.default || getSession().workspaces.selected
  }

  static getWorkspaceIdForQuickStart() {
    const session = getSession()
    const { adminOf = [] } = session.workspaces

    if (superAdmin()) {
      return this.getAssignedWorkspaceId()
    }

    if (this.isAdmin() && adminOf.length > 0) {
      const assignedWorkspace = this.getAssignedWorkspaceId()

      if (assignedWorkspace && adminOf.includes(assignedWorkspace)) {
        return assignedWorkspace
      }

      return adminOf[0]
    }

    throw new Error(`There's no workspace assigned to this session`)
  }

  static isSignatureTourCompleted() {
    return getSession().actionsCompleted?.includes(SessionActions.EmailSignatureTourCompleted)
  }

  static createEmptySession(): ISession {
    return {
      user: {
        displayName: '',
      },
      workspaces: {
        adminOf: [],
        assignedTo: [],
        default: '',
        selected: '',
        selectedType: 'CRM',
      },
      products: [],
      licences: {
        mildMeetings: {
          status: ProductStatus.Inactive,
        },
        meetings: {
          status: ProductStatus.Inactive,
        },
        events: {
          status: ProductStatus.Inactive,
        },
        concierge: {
          status: ProductStatus.Inactive,
        },
        chat: {
          status: ProductStatus.Inactive,
        },
        distro: {
          status: ProductStatus.Inactive,
        },
      },
    }
  }
}

export type SessionType = typeof Session
